import { Fragment, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar/Avatar";
import { MenuOption } from "../../components/Menu/MenuOption";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { type PopoverPlacement, usePopoverPosition } from "../../hooks/usePopoverPosition";

export interface SidebarAccountMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  /** The item opens a dialog (e.g. Send feedback). */
  opensDialog?: boolean;
  /** Sets the item apart with a divider above it (e.g. Sign out). */
  separatorBefore?: boolean;
}

/**
 * The avatar button and the account menu it opens (Settings, Send feedback, Sign out...). Unlike ContextMenu, which opens at a
 * point below its trigger, the menu is anchored to the avatar, so at the bottom of the sidebar it opens upward
 * (or beside the rail). It renders in place, not in a portal, so it stays above a modal <dialog> (a phone drawer)
 * the sidebar may be in.
 */
export function SidebarAccountMenu({
  userInitials,
  label,
  items,
  placement,
  tooltipPlacement,
  anchorClassName = "inline-flex",
  triggerClassName = "",
}: Readonly<{
  userInitials: string;
  /** The avatar button's name and tooltip, which also names the menu. */
  label: string;
  items: SidebarAccountMenuItem[];
  placement: PopoverPlacement;
  tooltipPlacement?: "top" | "right";
  /** The box the menu is placed against, around the avatar: e.g. the rail's full width, to open beside the rail. */
  anchorClassName?: string;
  triggerClassName?: string;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const triggerId = useId();
  const menuId = useId();
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement,
    align: "start",
  });

  const open = (index: number) => {
    setActiveIndex(index);
    setIsOpen(true);
  };
  const close = (returnFocus: boolean) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, panelRef]);

  // Opened with the mouse, no item is active until one is hovered or an arrow key is pressed; the panel holds focus.
  // The panel starts hidden while it's positioned, and hidden elements can't take focus: wait until it shows.
  const panelVisible = style.visibility === "visible";
  useEffect(() => {
    if (isOpen && panelVisible) (itemRefs.current[activeIndex] ?? panelRef.current)?.focus();
  }, [isOpen, panelVisible, activeIndex, panelRef]);

  // Closed (and focus back on the avatar) before the item runs, so a dialog it opens returns focus there,
  // and a page screenshot taken as that dialog opens doesn't show the menu.
  const select = (item: SidebarAccountMenuItem) => {
    close(true);
    item.onSelect();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      open(event.key === "ArrowDown" ? 0 : items.length - 1);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    const last = items.length - 1;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, last));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(last);
        break;
      case "Escape":
        // Only the menu: not a drawer or dialog the sidebar is in.
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case "Tab":
        // Back on the avatar, the browser's own Tab then moves on from it.
        close(true);
        break;
    }
  };

  return (
    <>
      <span ref={anchorRef} className={anchorClassName}>
        <Tooltip content={label} placement={tooltipPlacement} disabled={isOpen}>
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuId : undefined}
            onClick={(event) => (isOpen ? close(false) : open(event.currentTarget.matches(":focus-visible") ? 0 : -1))}
            onKeyDown={handleTriggerKeyDown}
            className={`cursor-pointer rounded-full transition-opacity hover:opacity-90 btn-focus-ring ${triggerClassName}`}
          >
            <Avatar initials={userInitials} size="sm" />
          </button>
        </Tooltip>
      </span>
      {isOpen && (
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-labelledby={triggerId}
          onKeyDown={handleMenuKeyDown}
          style={style}
          className="z-[110] w-48 overflow-hidden rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg outline-none"
        >
          {items.map((item, index) => (
            <Fragment key={item.key}>
              {item.separatorBefore && index > 0 && (
                <div role="separator" className="my-1 border-t border-border-subtle" />
              )}
              <MenuOption
                id={`${menuId}-${item.key}`}
                icon={item.icon}
                highlighted={index === activeIndex}
                aria-haspopup={item.opensDialog ? "dialog" : undefined}
                optionRef={(node) => {
                  itemRefs.current[index] = node;
                }}
                onHighlight={() => setActiveIndex(index)}
                onSelect={() => select(item)}
              >
                {item.label}
              </MenuOption>
            </Fragment>
          ))}
        </div>
      )}
    </>
  );
}
